import * as React from "react";
import { Script } from "gatsby";
import { useGclid } from "../useGclid";
import { createComponentProps } from "@/utils/componentDataAttributes";
import { useLayoutData } from "./useLayoutData";
import {
  navbarComponentMap,
  getNavBarComponent,
  getBannerComponent,
  getFooterComponent,
  BannerStyle,
} from "./componentRegistry";
import { TopUtilityBanner } from "@/components/banner/TopUtilityBanner";

interface LayoutProps {
  children: React.ReactNode;
  /** Override banner style for testing/storybook. Options: 'bigBold' | 'modernClean' | 'traditional' */
  forceBannerStyle?: BannerStyle;
}

const Layout: React.FC<LayoutProps> = ({ children, forceBannerStyle }) => {
  useGclid();

  // data from Strapi
  const {
    footerContent,
    navbarData,
    bannerItems,
    components,
    useUtilityBanner,
    billingUrl,
    patientPortalUrl,
  } = useLayoutData();

  const navbarComponent = getNavBarComponent(components);
  const NavBarComponent = navbarComponentMap[navbarComponent.name];
  const navbarProps = createComponentProps(navbarComponent);
  const BannerComponent = getBannerComponent(navbarComponent.name, forceBannerStyle);
  const FooterComponent = getFooterComponent(navbarComponent.name);

  return (
    <div className="flex flex-col">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[100] focus:rounded-md focus:bg-primary focus:px-4 focus:py-2 focus:text-primary-foreground focus:shadow-lg"
      >
        Skip to main content
      </a>
      <header>
        {useUtilityBanner ? (
          <TopUtilityBanner
            phone={footerContent.contact.phone ?? null}
            socialLinks={footerContent.socialLinks}
            billingUrl={billingUrl}
            patientPortalUrl={patientPortalUrl}
            bannerItems={bannerItems}
          />
        ) : (
          <BannerComponent items={bannerItems} />
        )}
        <NavBarComponent
          services={navbarData.services}
          logoUrl={navbarData.logoUrl}
          primaryCTA={navbarData.primaryCTA}
          primaryCTAUrl={navbarData.primaryCTAUrl}
          navbarSecondaryCTA={navbarData.navbarSecondaryCTA}
          navbarSecondaryCTAUrl={navbarData.navbarSecondaryCTAUrl}
          enableResources={navbarData.enableResources}
          hasBanner={useUtilityBanner || bannerItems.length > 0}
          {...navbarProps}
        />
      </header>
      <Script
        src="https://www.google.com/recaptcha/enterprise.js?render=explicit"
        strategy="idle"
        onError={() => console.error("reCAPTCHA script failed to load")}
      />
      <main id="main-content" className="flex-1 pt-8">{children}</main>
      <FooterComponent content={footerContent} />
    </div>
  );
};

export default Layout;
